import { Component, OnInit, OnDestroy } from '@angular/core';
import { DownloadsService } from '../downloads.service';
import { NotificationService } from '../../shared/notification-service/notification.service';
import { DarkModeService } from '../../shared/dark-mode/dark-mode.service';
import {
  ColDef,
  GridApi,
  IServerSideDatasource,
  GetRowIdParams,
  GetRowIdFunc,
} from 'ag-grid-community';
import { DatePipe } from '@angular/common';
import { DownloadDeliveryCellComponent } from './download-delivery-cell.component';
import { ReportDeliveryStatus } from 'ldt-data-deliveries-api';
import * as dayjs from 'dayjs';

@Component({
    selector: 'app-downloads-list',
    templateUrl: './downloads-list.component.html',
    styleUrls: ['./downloads-list.component.scss'],
    providers: [DatePipe],
    standalone: false
})
export class DownloadsListComponent implements OnInit, OnDestroy {
  refreshing: boolean = false;
  hasData: boolean = true;

  constructor(
    private downloadsService: DownloadsService,
    private notify: NotificationService,
    public darkModeService: DarkModeService
  ) {}

  ngOnInit(): void {
    this.refreshing = true;

    // get the delivery ID from teh querystring if it exists
    const urlParams = new URLSearchParams(window.location.search);
    const deliveryId = urlParams.get('downloadDelivery');
    if (deliveryId) {
      try {
        this.downloadsService.getDownload(Number(deliveryId)).subscribe((delivery) => {
          this.downloadsService.downloadFile(delivery);
        });
      } catch (error) {
        this.notify.error('Failed to load download from querystring');
      }
    }
  }

  // AG-GRID configuration
  private simpleFilterParams: any = {
    filterOptions: ['contains'],
    suppressAndOrCondition: true,
  };
  serverSideDatasource: IServerSideDatasource = this.downloadsService.agGridDatasource();
  rowModelType: string = 'serverSide';
  cacheBlockSize = 100;
  defaultColDef: ColDef = {
    sortable: true,
    floatingFilter: false,
    resizable: true,
    filterParams: this.simpleFilterParams,
    menuTabs: ['generalMenuTab', 'filterMenuTab'],
    suppressHeaderMenuButton: true,
  };
  context: any = {
    paginationToken: undefined,
  };
  columnDefs: ColDef[] = [
    {
      headerName: 'Name',
      field: 'scheduled_report_metadata.report_definition.name',
      minWidth: 250,
      filter: false,
      sortable: false,
    },
    {
      headerName: 'Created',
      field: 'created_at',
      width: 180,
      sort: 'desc',
      cellRenderer: (params: any) => {
        return dayjs(params.data.created_at).utc().format('MM/DD/YYYY hh:mm A');
      },
    },
    {
      headerName: 'For',
      field: 'date_for',
      width: 180,
      cellRenderer: (params: any) => {
        const dateFor = params.data.date_for;
        if (!dateFor) return 'N/A';
        return dayjs(dateFor).utc().format('MM/DD/YYYY');
      },
    },
    {
      field: 'type',
      width: 180,
      cellRenderer: (params: any) => {
        return this.getTypeLabel(params.data.type);
      },
    },
    {
      headerName: 'Status',
      field: 'status',
      width: 150,
      cellRenderer: (params: any) => {
        const download = params.data;
        const statusClass = this.getStatusClass(download.status);
        const spinner =
          download.status === 'processing'
            ? '<div class="tw-ml-2 tw-inline-block tw-w-3 tw-h-3 tw-border-2 tw-border-solid tw-border-t-transparent tw-border-blue-500 tw-rounded-full tw-animate-spin"></div>'
            : '';
        return `
            <span class="tw-px-2 tw-inline-flex tw-items-center tw-text-xs tw-leading-5 tw-font-semibold tw-rounded ${statusClass}">
              ${download.status.charAt(0).toUpperCase() + download.status.slice(1)}
              ${spinner}
            </span>
          `;
      },
    },
    {
      headerName: 'Records',
      field: 'record_count',
      width: 150,
      valueFormatter: (params: any) => params.value?.toLocaleString(),
    },
    {
      headerName: 'Delivery',
      width: 220,
      filter: false,
      sortable: false,
      cellRenderer: DownloadDeliveryCellComponent,
      cellRendererParams: {
        onDownload: this.rowToDownload.bind(this),
      },
    },
  ];

  tooltipShowDelay = 200;
  private gridApi: GridApi;

  ngOnDestroy(): void {
    document.removeEventListener('download-file', (() => {}) as EventListener);
  }

  onGridReady(params: any) {
    this.gridApi = params.api;
    this.gridApi.sizeColumnsToFit();

    // responsive grid on resize
    window.addEventListener('resize', () => {
      setTimeout(() => {
        this.gridApi.sizeColumnsToFit();
      });
    });
  }

  refreshData(manual: boolean = true) {
    if (this.gridApi) {
      if (manual || Object.keys(this.gridApi.getFilterModel()).length == 0) {
        this.refreshing = true;
        this.gridApi.refreshServerSide({ purge: true });
      }
    }
  }

  onStoreRefreshed(event: any) {
    // Check if grid has any data
    const rowCount = this.gridApi?.getDisplayedRowCount() || 0;
    this.refreshing = false;
    this.hasData = rowCount > 0;
  }

  getStatusClass(status: ReportDeliveryStatus): string {
    switch (status) {
      case ReportDeliveryStatus.Complete: // complete
        return 'tw-bg-blue-100 tw-text-blue-800 dark:tw-bg-blue-900 dark:tw-text-blue-300';
      case ReportDeliveryStatus.InProgress: // in_progress
        return 'tw-bg-blue-100 tw-text-blue-800 dark:tw-bg-blue-900 dark:tw-text-blue-300';
      case ReportDeliveryStatus.Failed: // failed
        return 'tw-bg-red-100 tw-text-red-800 dark:tw-bg-red-900 dark:tw-text-red-300';
      case ReportDeliveryStatus.Scheduled: // scheduled
        return 'tw-bg-gray-100 tw-text-gray-800 dark:tw-bg-gray-900 dark:tw-text-gray-300';
      default:
        return '';
    }
  }

  getTypeLabel(type: string): string {
    return type === 'one_time'
      ? 'One-time Download'
      : type === 'scheduled'
        ? 'Subscription'
        : type === 'housefile'
          ? 'Housefile'
          : 'Unknown';
  }

  rowToDownload(event: any): void {
    const reportDelivery = event.rowData;

    if (reportDelivery.status !== ReportDeliveryStatus.Complete) {
      this.notify.warning('This download is not ready yet.');
      return;
    }

    this.downloadsService.downloadFile(reportDelivery);
  }

  public getRowId: GetRowIdFunc = (params: GetRowIdParams) => String(params.data.id);
}
